import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Badge,
  Button,
  Card,
  Select,
  SelectItem,
  Switch,
  Text,
  TextInput,
  Title,
} from '@tremor/react';
import {
  createCustomField,
  CustomFieldDefinition,
  CustomFieldScope,
  CustomFieldType,
  listCustomFields,
  updateCustomField,
} from '../../../network/customFields';

const FIELD_TYPES: { label: string; value: CustomFieldType }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Single Select', value: 'single_select' },
  { label: 'Multi Select', value: 'multi_select' },
];

const SCOPES: { label: string; value: CustomFieldScope }[] = [
  { label: 'People', value: 'person' },
  { label: 'Company', value: 'company' },
];

export function CustomFieldsForm() {
  const [scope, setScope] = useState<CustomFieldScope>('person');
  const [name, setName] = useState('');
  const [type, setType] = useState<CustomFieldType>('text');
  const [options, setOptions] = useState('');
  const [required, setRequired] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);

  const { data: response, refetch, isLoading } = useQuery({
    queryKey: ['custom-fields', scope],
    queryFn: () => listCustomFields({ scope }),
  });

  const fields: CustomFieldDefinition[] = response?.data || [];

  const { mutateAsync: createField, isLoading: isCreating } = useMutation(
    createCustomField,
  );
  const { mutateAsync: updateField, isLoading: isUpdating } = useMutation(
    ({ id, payload }: { id: string; payload: any }) =>
      updateCustomField(id, payload),
  );

  const parsedOptions = useMemo(() => {
    if (!options.trim()) return [];
    return options
      .split(',')
      .map((opt) => opt.trim())
      .filter(Boolean);
  }, [options]);

  const isSelectType = type === 'single_select' || type === 'multi_select';

  const resetForm = () => {
    setName('');
    setType('text');
    setOptions('');
    setRequired(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    if (editing?.system?.id) {
      const payload = {
        name: name.trim(),
        type,
        options: isSelectType ? parsedOptions : undefined,
        required,
        active: editing.details.active ?? true,
      };
      await updateField({
        id: editing.system.id,
        payload,
      });
    } else {
      const payload = {
        name: name.trim(),
        scope,
        type,
        options: isSelectType ? parsedOptions : undefined,
        required,
        active: true,
      };
      await createField(payload);
    }

    resetForm();
    refetch();
  };

  const handleEdit = (field: CustomFieldDefinition) => {
    setEditing(field);
    setScope(field.details.scope);
    setName(field.details.name);
    setType(field.details.type);
    setOptions((field.details.options || []).join(', '));
    setRequired(!!field.details.required);
  };

  const handleToggleActive = async (field: CustomFieldDefinition) => {
    await updateField({
      id: field.system.id,
      payload: { active: !field.details.active },
    });
    refetch();
  };

  return (
    <div className="space-y-6">
      <Card>
        <Title>Custom fields</Title>
        <Text className="mt-2">
          Define additional fields for people or company records.
        </Text>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Text className="mb-1">Scope</Text>
            <Select
              value={scope}
              onValueChange={(val) => setScope(val as CustomFieldScope)}
              disabled={!!editing}
            >
              {SCOPES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div>
            <Text className="mb-1">Field name</Text>
            <TextInput
              placeholder="e.g. LinkedIn URL"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Text className="mb-1">Field type</Text>
            <Select value={type} onValueChange={(val) => setType(val as CustomFieldType)}>
              {FIELD_TYPES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </div>

          {isSelectType && (
            <div>
              <Text className="mb-1">Options (comma-separated)</Text>
              <TextInput
                placeholder="e.g. Full-time, Part-time"
                value={options}
                onChange={(e) => setOptions(e.target.value)}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <Switch
              checked={required}
              onChange={() => setRequired((prev) => !prev)}
            />
            <Text>Required</Text>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || isCreating || isUpdating}
          >
            {editing ? 'Update field' : 'Create field'}
          </Button>
          {editing && (
            <Button variant="secondary" onClick={resetForm}>
              Cancel edit
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <Title>Existing fields</Title>
        <Text className="mt-2">
          {isLoading ? 'Loading fields…' : `${fields.length} fields`}
        </Text>

        <div className="mt-4 space-y-3">
          {fields.length === 0 && (
            <Text className="text-sm text-gray-500">No custom fields yet.</Text>
          )}

          {fields.map((field) => (
            <div
              key={field.system.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 p-3"
            >
              <div>
                <div className="flex items-center gap-2">
                  <Text className="font-medium">{field.details.name}</Text>
                  {!field.details.active && (
                    <Badge color="gray">Inactive</Badge>
                  )}
                  {field.details.required && <Badge color="red">Required</Badge>}
                </div>
                <Text className="text-sm text-gray-600">
                  {field.details.type.replace('_', ' ')}
                </Text>
                {field.details.options?.length ? (
                  <Text className="text-xs text-gray-500">
                    Options: {field.details.options.join(', ')}
                  </Text>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() => handleEdit(field)}
                >
                  Edit
                </Button>
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() => handleToggleActive(field)}
                >
                  {field.details.active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

